import { type Registry as Services, service } from '@ember/service';
import { underscore } from '@ember/string';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationAdapter from 'lumen/adapters/application';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import type { Promise as RsvpPromise } from 'rsvp';

const PROFILES_BASE = 'net-revenue/cost-profiles';

export default class CostSettingProfileAdapter extends ApplicationAdapter {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  namespace = APIGATEWAY;

  // The base type only allows 3 args, but this needs 5 to reach
  // `requestType` — `ApplicationAdapter.buildURL` has the same gap.
  // @ts-expect-error: Property 'buildURL' in type is not assignable to the same property in base type.
  buildURL(
    modelName: string,
    id: string,
    snapshot: Snapshot<unknown>,
    requestType: string,
    query?: Record<string, unknown>,
  ): string {
    // Same mismatch as above.
    // @ts-expect-error: Expected 3 arguments, but got 5
    const baseUrl = super.buildURL(modelName, id, snapshot, requestType, query);

    const url = new URL(baseUrl, this.host);
    const { market } = this.hotelAndMarketService;
    if (market) {
      url.searchParams.set('market_id', market.id!);
    }

    return url.toString();
  }

  // Stops handleQuery from pluralising the model name in the URL.
  pathForType(): string {
    return PROFILES_BASE;
  }

  query(
    store: Store,
    type: ModelSchema,
    query: Record<string, unknown>,
  ): ReturnType<ApplicationAdapter['handleQuery']> {
    return this.handleQuery(store, type, { ...query, namespace: APIGATEWAY });
  }

  updateRecord(
    store: Store,
    type: ModelSchema,
    snapshot: Snapshot,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): RsvpPromise<any> {
    const url = this.buildURL(
      type.modelName,
      snapshot.id ?? '',
      snapshot,
      'updateRecord',
    );

    const serializedSnapshot = snapshot.serialize({
      includeId: true,
    }) as Record<string, unknown>;
    // `changedAttributes()` doesn't track relationships, so `costs` must be
    // added by hand or an edit would silently drop the commission.
    const keysToUpdate = new Set([
      ...Object.keys(snapshot.changedAttributes()),
      'costs',
    ]);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const payloadKey = store
      .serializerFor(snapshot.modelName)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .payloadKeyFromModelName(snapshot.modelName);

    const body: Record<string, unknown> = {};
    for (const key of keysToUpdate) {
      const apiKey = underscore(key);
      body[apiKey] = serializedSnapshot[apiKey];
    }

    // `ajax`'s type doesn't model the `{ data }` shape it actually sends.
    // @ts-expect-error: Incorrect type
    return this.ajax(url, 'PATCH', { data: { [payloadKey]: body } });
  }

  urlForCreateRecord(): string {
    return `${this.host}/${APIGATEWAY}/${PROFILES_BASE}`;
  }

  urlForDeleteRecord(id: string): string {
    return `${this.host}/${APIGATEWAY}/${PROFILES_BASE}/${id}`;
  }

  urlForUpdateRecord(id: string): string {
    return `${this.host}/${APIGATEWAY}/${PROFILES_BASE}/${id}`;
  }
}
